import { Dispatch, useRef, useState } from 'react'

import { requestPlatformAccess } from '../../../../services/api/request-access'
import { readAccessRequestRetryDate, rememberAccessRequest } from '../../../../util/access-requests'
import { trackSignup } from '../analytics'
import { ConnectPlatform, NewSignupAction } from '../types'

export interface IUseSignupAccessRequestParams {
  localDispatch: Dispatch<NewSignupAction>
  readProviderToken: (platform: ConnectPlatform) => string | undefined
}

export interface IRequestAccessParams {
  platform: ConnectPlatform
  email: string
}

export interface ISignupAccessRequest {
  isRequestingAccess: boolean
  requestAccess: (params: IRequestAccessParams) => Promise<void>
}

/**
 * The ask a visitor whose team is already here can make without leaving the page. It goes with
 * the provider sign-in that found the team, so the endpoint reaches that company and no other.
 */
export const useSignupAccessRequest = ({ localDispatch, readProviderToken }: IUseSignupAccessRequestParams): ISignupAccessRequest => {
  const [isRequestingAccess, setIsRequestingAccess] = useState(false)
  // The ref, not the state: a second click can land before the disabled link has rendered.
  const inFlightRef = useRef(false)

  const requestAccess = async ({ platform, email }: IRequestAccessParams) => {
    if (inFlightRef.current) return
    trackSignup('SIGNUP_REQUEST_ACCESS_CLICKED', { platform })

    // The endpoint drops a repeat inside its cooldown and answers as if it had sent one, so this
    // browser's own record is the only thing that can say a request already went out.
    const retryOn = email ? readAccessRequestRetryDate(email) : null
    if (retryOn) {
      trackSignup('SIGNUP_REQUEST_ACCESS_SKIPPED', { platform })
      localDispatch({ type: 'accessRequestResolved', banner: { kind: 'accessAlreadyRequested', retryOn: retryOn.toISOString() } })
      return
    }

    inFlightRef.current = true
    setIsRequestingAccess(true)
    try {
      const token = readProviderToken(platform)
      if (!token) {
        throw new Error('No provider sign-in to send')
      }
      await requestPlatformAccess({ platform, token })
      if (email) {
        rememberAccessRequest(email)
      }
      trackSignup('SIGNUP_REQUEST_ACCESS_SENT', { platform })
      localDispatch({ type: 'accessRequestResolved', banner: { kind: 'accessRequested' } })
    } catch (error) {
      trackSignup('SIGNUP_REQUEST_ACCESS_FAILED', { platform })
      localDispatch({ type: 'accessRequestResolved', banner: { kind: 'accessRequestFailed' } })
    } finally {
      inFlightRef.current = false
      setIsRequestingAccess(false)
    }
  }

  return { isRequestingAccess, requestAccess }
}
